import type { FieldPath } from 'react-hook-form';
import { z } from 'zod';
import { createDateOnly } from './utils';

const assessmentRunSchema = z.object({
    id: z.string().optional(),
    name: z.string().min(1, { message: 'Assessment run name is required' }),
    startDate: z.date({ required_error: 'Start date is required' }),
    endDate: z.date({ required_error: 'End date is required' }),
});

export type AssessmentRunSchema = z.infer<typeof assessmentRunSchema>;

export const assessmentRunsFormSchema = z.object({
    id: z.string().optional(),
    groupId: z.string().min(1, { message: 'Group selection is required' }),
    groupName: z.string().optional(),
    assessmentRuns: z
        .array(assessmentRunSchema)
        .min(1, { message: 'At least one assessment run is required' })
        .refine(
            runs => {
                const names = runs.map(run => run.name.trim().toLowerCase()).filter(name => name);
                return names.length === new Set(names).size;
            },
            { message: 'Assessment run names must be unique' }
        )
        .superRefine((runs, ctx) => {
            runs.forEach((run, index) => {
                if (run.startDate && run.endDate && run.startDate >= run.endDate) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Start date must be before end date',
                        path: [index, 'startDate'],
                    });
                }
            });
        }),
});

export type AssessmentRunsFormData = z.infer<typeof assessmentRunsFormSchema>;

export const defaultAssessmentRunsFormValues: AssessmentRunsFormData = {
    groupId: '',
    groupName: '',
    assessmentRuns: [],
};

export const getFieldsToValidate = (): FieldPath<AssessmentRunsFormData>[] => {
    return ['groupId', 'assessmentRuns'];
};

// Helper function to create a new empty assessment run
export const createEmptyAssessmentRun = (prevDate: Date, days: number) => ({
    name: '',
    startDate: createDateOnly(prevDate, 1),
    endDate: createDateOnly(prevDate, days + 1),
});

// Helper function to validate date range
export const validateDateRange = (startDate: Date, endDate: Date): boolean => {
    if (!startDate || !endDate) {
        return true;
    }
    return startDate < endDate;
};
